import type { Booking, Pitch } from "@/types/camping";
import { todayLocal } from "@/lib/bookingDates";
import { BLOCKING_STATUSES } from "@/lib/pitchAvailability";

/** Abgeschlossene Aufenthalte – nur für vergangene Tage in der Timeline relevant. */
export const HISTORICAL_OCCUPANCY_STATUSES = new Set(["checked_out"]);

export type PitchTimelineSegmentKind = "free" | "occupied" | "blocked_pitch";

export interface PitchTimelineSegment {
  kind: PitchTimelineSegmentKind;
  from: string;
  to: string;
  booking?: Booking;
}

export interface PitchPlannerRow {
  pitch: Pitch;
  segments: PitchTimelineSegment[];
}

function compareIsoDates(a: string, b: string): number {
  return a.localeCompare(b);
}

function bookingOverlapsRange(booking: Booking, rangeFrom: string, rangeToExclusive: string): boolean {
  return (
    booking.arrival_date < rangeToExclusive &&
    booking.departure_date > rangeFrom
  );
}

function clipSegmentStart(from: string, rangeFrom: string): string {
  return compareIsoDates(from, rangeFrom) < 0 ? rangeFrom : from;
}

function clipSegmentEnd(to: string, rangeToExclusive: string): string {
  return compareIsoDates(to, rangeToExclusive) > 0 ? rangeToExclusive : to;
}

function pastRangeEndExclusive(rangeToExclusive: string, today: string): string {
  return compareIsoDates(rangeToExclusive, today) <= 0 ? rangeToExclusive : today;
}

function bookingCountsAsOccupied(
  booking: Booking,
  rangeFrom: string,
  rangeToExclusive: string,
  today: string,
): boolean {
  if (!booking.pitch_id) return false;
  if (!bookingOverlapsRange(booking, rangeFrom, rangeToExclusive)) return false;
  if (BLOCKING_STATUSES.has(booking.status)) return true;
  if (!HISTORICAL_OCCUPANCY_STATUSES.has(booking.status)) return false;

  const pastEnd = pastRangeEndExclusive(rangeToExclusive, today);
  if (compareIsoDates(rangeFrom, pastEnd) >= 0) return false;
  return booking.arrival_date < pastEnd && booking.departure_date > rangeFrom;
}

function pushFreeSegment(
  segments: PitchTimelineSegment[],
  from: string,
  to: string,
): void {
  if (compareIsoDates(from, to) >= 0) return;
  segments.push({ kind: "free", from, to });
}

/** Belegungs- und Freizeiträume pro Platz im Halboffenen-Intervall [rangeFrom, rangeToExclusive). */
export function buildPitchTimeline(
  pitch: Pitch,
  bookings: Booking[],
  rangeFrom: string,
  rangeToExclusive: string,
  today?: string,
): PitchTimelineSegment[] {
  if (compareIsoDates(rangeFrom, rangeToExclusive) >= 0) return [];

  if (!pitch.is_active || pitch.is_blocked) {
    return [{ kind: "blocked_pitch", from: rangeFrom, to: rangeToExclusive }];
  }

  const referenceToday = today ?? todayLocal();
  const blocking = bookings
    .filter(
      (b) =>
        b.pitch_id === pitch.id &&
        bookingCountsAsOccupied(b, rangeFrom, rangeToExclusive, referenceToday),
    )
    .sort((a, b) => compareIsoDates(a.arrival_date, b.arrival_date));

  const segments: PitchTimelineSegment[] = [];
  let cursor = rangeFrom;

  for (const booking of blocking) {
    const occupiedFrom = clipSegmentStart(booking.arrival_date, rangeFrom);
    const occupiedTo = clipSegmentEnd(booking.departure_date, rangeToExclusive);
    pushFreeSegment(segments, cursor, occupiedFrom);
    if (compareIsoDates(occupiedFrom, occupiedTo) < 0) {
      segments.push({
        kind: "occupied",
        from: occupiedFrom,
        to: occupiedTo,
        booking,
      });
    }
    if (compareIsoDates(cursor, occupiedTo) < 0) {
      cursor = occupiedTo;
    }
  }

  pushFreeSegment(segments, cursor, rangeToExclusive);
  return segments;
}

export function buildPitchPlannerRows(
  pitches: Pitch[],
  bookings: Booking[],
  rangeFrom: string,
  rangeToExclusive: string,
  today?: string,
): PitchPlannerRow[] {
  const active = pitches.filter((p) => p.is_active);
  const inactive = pitches.filter((p) => !p.is_active);

  const rows = [...active, ...inactive].map((pitch) => ({
    pitch,
    segments: buildPitchTimeline(pitch, bookings, rangeFrom, rangeToExclusive, today),
  }));

  return rows.sort((a, b) => a.pitch.code.localeCompare(b.pitch.code, "de"));
}
